import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { AvatarUploader } from "@/components/UserAvatar";

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "attendant", department: "" });
  const [departments, setDepartments] = useState([]);

  useEffect(() => { api.get("/departments").then(({ data }) => setDepartments(data)).catch(() => {}); }, []);

  const updateUser = async (u, patch) => {
    try {
      await api.patch(`/users/${u.id}`, patch);
      toast.success("Atendente atualizado");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao atualizar"); }
  };

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      if (e.response?.status === 403) toast.error("Apenas administradores podem gerenciar atendentes.");
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", { ...form, department: form.department || null });
      toast.success("Atendente cadastrado");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "attendant", department: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Falha ao criar");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remover ${u.name}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Removido");
    load();
  };

  if (user?.role !== "admin") {
    return <div className="p-10 text-center text-muted-foreground">Acesso restrito ao administrador.</div>;
  }

  return (
    <div className="h-full overflow-auto p-6 lg:p-10">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="heading text-3xl font-extrabold">Atendentes</h1>
            <p className="text-sm text-muted-foreground mt-1">Gerencie sua equipe de atendimento.</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700" data-testid="user-create-button">
                <UserPlus className="w-4 h-4 mr-2" /> Novo atendente
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Cadastrar atendente</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Nome</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
                <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Senha inicial</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
                <div>
                  <Label>Perfil</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attendant">Atendente</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Departamento</Label>
                  <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })} data-testid="department-select">
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Selecione..." /></SelectTrigger>
                    <SelectContent>
                      {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-emerald-600 hover:bg-emerald-700">Cadastrar</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardContent className="p-0">
            {users.map((u) => (
              <div key={u.id} className="flex items-center gap-4 p-4 border-b border-border">
                <AvatarUploader user={u} endpoint={`/users/${u.id}/avatar`} onChange={load} testid={`user-avatar-upload-${u.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                </div>
                <Badge variant={u.role === "admin" ? "default" : "secondary"} className={u.role === "admin" ? "bg-emerald-600" : ""}>{u.role}</Badge>
                <Select value={u.department || "none"} onValueChange={(v) => updateUser(u, { department: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-8 w-44 text-xs" data-testid="user-department-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sem setor</SelectItem>
                    {departments.map((d) => <SelectItem key={d.id} value={d.name}>{d.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {u.id !== user.id && (
                  <label className="flex items-center gap-1.5 text-[11px] text-muted-foreground" title="Exceção: pode atender sem registrar ponto">
                    <input type="checkbox" checked={!!u.timeclock_exempt} onChange={(e) => updateUser(u, { timeclock_exempt: e.target.checked })} data-testid="user-timeclock-exempt" />
                    Isento de ponto
                  </label>
                )}
                {u.id !== user.id && (
                  <Select value={u.role} onValueChange={(v) => updateUser(u, { role: v })}>
                    <SelectTrigger className="h-8 w-32 text-xs" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="attendant">Atendente</SelectItem>
                      <SelectItem value="admin">Administrador</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {u.id !== user.id && (
                  <Button variant="ghost" size="sm" onClick={() => remove(u)} className="text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
